"use client";

import { db } from "@/lib/firebase";
import { formatFirestoreDocument } from "@/lib/firestore";
import { subscribeShared } from "@/lib/firestore-listener-registry";
import type { PoolRequest, TournamentPoolEntry } from "@/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  collection,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { useEffect, useMemo, useRef } from "react";
import { usePageVisibility } from "./useVisibilityPausedQuery";

/**
 * Live subscription to all active pool entries for a tournament.
 * Used by the /pool page to render the list of free players.
 */
export function useTournamentPoolEntries(tournamentId: string | undefined) {
  const queryClient = useQueryClient();
  const isVisible = usePageVisibility();
  const queryKey = useMemo(
    () => ["poolEntries", tournamentId] as const,
    [tournamentId],
  );
  const pendingRef = useRef<{
    resolve: (entries: TournamentPoolEntry[]) => void;
    reject: (error: Error) => void;
  } | null>(null);

  useEffect(() => {
    if (!isVisible || !tournamentId) return;

    const unsubscribe = subscribeShared<TournamentPoolEntry[]>(
      `poolEntries:${tournamentId}`,
      (onData, onError) => {
        const q = query(
          collection(db, "tournamentPoolEntries"),
          where("tournamentId", "==", tournamentId),
          where("status", "==", "active"),
        );
        return onSnapshot(
          q,
          (snapshot) => {
            const entries = snapshot.docs.map((doc) =>
              formatFirestoreDocument(doc),
            ) as TournamentPoolEntry[];
            onData(entries);
          },
          onError,
        );
      },
      {
        onData: (entries) => {
          if (pendingRef.current) {
            pendingRef.current.resolve(entries);
            pendingRef.current = null;
          }
          queryClient.setQueryData(queryKey, entries);
        },
        onError: (error) => {
          console.error("Error subscribing to pool entries:", error);
          if (pendingRef.current) {
            pendingRef.current.reject(error);
            pendingRef.current = null;
          }
        },
      },
    );

    return () => {
      unsubscribe();
      if (pendingRef.current) {
        const cached =
          queryClient.getQueryData<TournamentPoolEntry[]>(queryKey);
        pendingRef.current.resolve(cached ?? []);
        pendingRef.current = null;
      }
    };
  }, [tournamentId, queryClient, queryKey, isVisible]);

  return useQuery({
    queryKey,
    queryFn: () => {
      return new Promise<TournamentPoolEntry[]>((resolve, reject) => {
        const cached =
          queryClient.getQueryData<TournamentPoolEntry[]>(queryKey);
        if (cached !== undefined) {
          resolve(cached);
          return;
        }
        const timeoutId = setTimeout(() => {
          if (pendingRef.current) {
            pendingRef.current.resolve([]);
            pendingRef.current = null;
          }
        }, 10_000);
        pendingRef.current = {
          resolve: (entries) => {
            clearTimeout(timeoutId);
            resolve(entries);
          },
          reject: (error) => {
            clearTimeout(timeoutId);
            reject(error);
          },
        };
      });
    },
    enabled: !!tournamentId,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 5,
  });
}

/**
 * Live subscription to the current user's active pool entry for a tournament.
 * Returns null when the user is not in the pool.
 */
export function useCurrentUserPoolEntry(
  tournamentId: string | undefined,
  userId: string | undefined,
) {
  const queryClient = useQueryClient();
  const isVisible = usePageVisibility();
  const queryKey = useMemo(
    () => ["poolEntries", tournamentId, "self", userId] as const,
    [tournamentId, userId],
  );
  const pendingRef = useRef<{
    resolve: (entry: TournamentPoolEntry | null) => void;
    reject: (error: Error) => void;
  } | null>(null);

  useEffect(() => {
    if (!isVisible || !tournamentId || !userId) return;

    const unsubscribe = subscribeShared<TournamentPoolEntry | null>(
      `poolEntries:${tournamentId}:self:${userId}`,
      (onData, onError) => {
        const q = query(
          collection(db, "tournamentPoolEntries"),
          where("tournamentId", "==", tournamentId),
          where("userId", "==", userId),
          where("status", "==", "active"),
        );
        return onSnapshot(
          q,
          (snapshot) => {
            const entry =
              snapshot.docs.length > 0
                ? (formatFirestoreDocument(
                    snapshot.docs[0],
                  ) as TournamentPoolEntry)
                : null;
            onData(entry);
          },
          onError,
        );
      },
      {
        onData: (entry) => {
          if (pendingRef.current) {
            pendingRef.current.resolve(entry);
            pendingRef.current = null;
          }
          queryClient.setQueryData(queryKey, entry);
        },
        onError: (error) => {
          console.error("Error subscribing to user pool entry:", error);
          if (pendingRef.current) {
            pendingRef.current.reject(error);
            pendingRef.current = null;
          }
        },
      },
    );

    return () => {
      unsubscribe();
      if (pendingRef.current) {
        const cached = queryClient.getQueryData<TournamentPoolEntry | null>(
          queryKey,
        );
        pendingRef.current.resolve(cached ?? null);
        pendingRef.current = null;
      }
    };
  }, [tournamentId, userId, queryClient, queryKey, isVisible]);

  return useQuery({
    queryKey,
    queryFn: () => {
      return new Promise<TournamentPoolEntry | null>((resolve, reject) => {
        const cached = queryClient.getQueryData<TournamentPoolEntry | null>(
          queryKey,
        );
        if (cached !== undefined) {
          resolve(cached);
          return;
        }
        const timeoutId = setTimeout(() => {
          if (pendingRef.current) {
            pendingRef.current.resolve(null);
            pendingRef.current = null;
          }
        }, 10_000);
        pendingRef.current = {
          resolve: (entry) => {
            clearTimeout(timeoutId);
            resolve(entry);
          },
          reject: (error) => {
            clearTimeout(timeoutId);
            reject(error);
          },
        };
      });
    },
    enabled: !!tournamentId && !!userId,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 5,
  });
}

/**
 * Live subscription to pending team-pickup pool requests sent from a given
 * team. Used by the captain in the PoolList to show "Pending" / "Cancel"
 * states on free players they've already invited.
 */
export function useTeamOutgoingPoolPicks(
  tournamentId: string | undefined,
  teamId: string | undefined,
) {
  const queryClient = useQueryClient();
  const isVisible = usePageVisibility();
  const queryKey = useMemo(
    () => ["poolRequests", tournamentId, "team", teamId] as const,
    [tournamentId, teamId],
  );
  const pendingRef = useRef<{
    resolve: (requests: PoolRequest[]) => void;
    reject: (error: Error) => void;
  } | null>(null);

  useEffect(() => {
    if (!isVisible || !tournamentId || !teamId) return;

    const unsubscribe = subscribeShared<PoolRequest[]>(
      `poolRequests:${tournamentId}:team:${teamId}`,
      (onData, onError) => {
        const q = query(
          collection(db, "poolRequests"),
          where("tournamentId", "==", tournamentId),
          where("fromTeamId", "==", teamId),
          where("status", "==", "pending"),
        );
        return onSnapshot(
          q,
          (snapshot) => {
            const requests = snapshot.docs.map((doc) =>
              formatFirestoreDocument(doc),
            ) as PoolRequest[];
            onData(requests);
          },
          onError,
        );
      },
      {
        onData: (requests) => {
          if (pendingRef.current) {
            pendingRef.current.resolve(requests);
            pendingRef.current = null;
          }
          queryClient.setQueryData(queryKey, requests);
        },
        onError: (error) => {
          console.error("Error subscribing to team-pickup requests:", error);
          if (pendingRef.current) {
            pendingRef.current.reject(error);
            pendingRef.current = null;
          }
        },
      },
    );

    return () => {
      unsubscribe();
      if (pendingRef.current) {
        const cached = queryClient.getQueryData<PoolRequest[]>(queryKey);
        pendingRef.current.resolve(cached ?? []);
        pendingRef.current = null;
      }
    };
  }, [tournamentId, teamId, queryClient, queryKey, isVisible]);

  return useQuery({
    queryKey,
    queryFn: () => {
      return new Promise<PoolRequest[]>((resolve, reject) => {
        const cached = queryClient.getQueryData<PoolRequest[]>(queryKey);
        if (cached !== undefined) {
          resolve(cached);
          return;
        }
        const timeoutId = setTimeout(() => {
          if (pendingRef.current) {
            pendingRef.current.resolve([]);
            pendingRef.current = null;
          }
        }, 10_000);
        pendingRef.current = {
          resolve: (requests) => {
            clearTimeout(timeoutId);
            resolve(requests);
          },
          reject: (error) => {
            clearTimeout(timeoutId);
            reject(error);
          },
        };
      });
    },
    enabled: !!tournamentId && !!teamId,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 5,
  });
}

/**
 * Live subscription to pool requests where the current user is either sender
 * or recipient (pending only). Returned grouped for easy consumption.
 */
export function useUserPoolRequests(
  tournamentId: string | undefined,
  userId: string | undefined,
) {
  const queryClient = useQueryClient();
  const isVisible = usePageVisibility();
  const queryKey = useMemo(
    () => ["poolRequests", tournamentId, "user", userId] as const,
    [tournamentId, userId],
  );

  type RequestState = {
    incoming: PoolRequest[];
    outgoing: PoolRequest[];
  };

  const pendingRef = useRef<{
    resolve: (state: RequestState) => void;
    reject: (error: Error) => void;
  } | null>(null);

  useEffect(() => {
    if (!isVisible || !tournamentId || !userId) return;

    // Local mirror of the latest snapshot from each shared listener. Kept in
    // refs so the closures below can publish the most recent combined value
    // without re-subscribing.
    const latest: RequestState = { incoming: [], outgoing: [] };
    let hasIncoming = false;
    let hasOutgoing = false;

    const publish = () => {
      if (!hasIncoming || !hasOutgoing) return;
      if (pendingRef.current) {
        pendingRef.current.resolve(latest);
        pendingRef.current = null;
      }
      // React Query treats reference equality as same data; clone to trigger
      // re-renders.
      queryClient.setQueryData(queryKey, {
        incoming: [...latest.incoming],
        outgoing: [...latest.outgoing],
      });
    };

    const unsubIncoming = subscribeShared<PoolRequest[]>(
      `poolRequests:${tournamentId}:to:${userId}`,
      (onData, onError) => {
        const incomingQuery = query(
          collection(db, "poolRequests"),
          where("tournamentId", "==", tournamentId),
          where("toUserId", "==", userId),
          where("status", "==", "pending"),
        );
        return onSnapshot(
          incomingQuery,
          (snapshot) => {
            onData(
              snapshot.docs.map((doc) =>
                formatFirestoreDocument(doc),
              ) as PoolRequest[],
            );
          },
          onError,
        );
      },
      {
        onData: (incoming) => {
          latest.incoming = incoming;
          hasIncoming = true;
          publish();
        },
        onError: (error) => {
          console.error("Error subscribing to incoming pool requests:", error);
        },
      },
    );

    const unsubOutgoing = subscribeShared<PoolRequest[]>(
      `poolRequests:${tournamentId}:from:${userId}`,
      (onData, onError) => {
        const outgoingQuery = query(
          collection(db, "poolRequests"),
          where("tournamentId", "==", tournamentId),
          where("fromUserId", "==", userId),
          where("status", "==", "pending"),
        );
        return onSnapshot(
          outgoingQuery,
          (snapshot) => {
            onData(
              snapshot.docs.map((doc) =>
                formatFirestoreDocument(doc),
              ) as PoolRequest[],
            );
          },
          onError,
        );
      },
      {
        onData: (outgoing) => {
          latest.outgoing = outgoing;
          hasOutgoing = true;
          publish();
        },
        onError: (error) => {
          console.error("Error subscribing to outgoing pool requests:", error);
        },
      },
    );

    return () => {
      unsubIncoming();
      unsubOutgoing();
      if (pendingRef.current) {
        pendingRef.current.resolve(latest);
        pendingRef.current = null;
      }
    };
  }, [tournamentId, userId, queryClient, queryKey, isVisible]);

  return useQuery({
    queryKey,
    queryFn: () => {
      return new Promise<RequestState>((resolve, reject) => {
        const cached = queryClient.getQueryData<RequestState>(queryKey);
        if (cached !== undefined) {
          resolve(cached);
          return;
        }
        const timeoutId = setTimeout(() => {
          if (pendingRef.current) {
            pendingRef.current.resolve({ incoming: [], outgoing: [] });
            pendingRef.current = null;
          }
        }, 10_000);
        pendingRef.current = {
          resolve: (s) => {
            clearTimeout(timeoutId);
            resolve(s);
          },
          reject: (error) => {
            clearTimeout(timeoutId);
            reject(error);
          },
        };
      });
    },
    enabled: !!tournamentId && !!userId,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 5,
  });
}
